import React, { useCallback, useMemo, useState } from 'react'
import { Button, Col, Pagination, Popconfirm, Row } from 'antd'
import { isEmpty, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { Select, TreeSelect } from '../../../common/components/sections'

const LocationsTableFooter = ({
  isEditDisabled,
  mainLocationTypeOptions,
  additionalLocationTypeOptions,
  cuisineTypeOptions,
  awardOptions,
  onBulkUpdateLocations,
  paginationConfig,
}) => {
  const [cuisineType, setCuisineType] = useState(null)
  const [mainLocationType, setMainLocationType] = useState(null)
  const [additionalLocationTypes, setAdditionalLocationTypes] = useState([])
  const [awards, setAwards] = useState([])

  const resetState = useCallback(() => {
    setCuisineType(null)
    setMainLocationType(null)
    setAdditionalLocationTypes([])
    setAwards([])
  }, [setCuisineType, setMainLocationType])

  const onSave = useCallback(() => {
    onBulkUpdateLocations({
      cuisineType,
      mainLocationType,
      additionalLocationTypes,
      awards,
    })
    resetState()
  }, [
    onBulkUpdateLocations,
    cuisineType,
    mainLocationType,
    additionalLocationTypes,
    awards,
    resetState,
  ])

  const onCancel = useCallback(() => {
    onBulkUpdateLocations({})
    resetState()
  }, [onBulkUpdateLocations, resetState])

  const isSaveDisabled = useMemo(
    () =>
      isEditDisabled ||
      (isNil(cuisineType) &&
        isNil(mainLocationType) &&
        isNil(additionalLocationTypes) &&
        isNil(awards)),
    [
      isEditDisabled,
      cuisineType,
      mainLocationType,
      additionalLocationTypes,
      awards,
    ],
  )

  const isSaveButtonVisible = useMemo(
    () => !isEmpty(additionalLocationTypes) || !isEmpty(awards),
    [additionalLocationTypes, awards],
  )

  return (
    <Row gutter={[16]} justify="end" style={{ marginTop: '1rem' }}>
      <Col span={4}>
        <Select
          showSearch
          allowClear
          value={mainLocationType}
          options={mainLocationTypeOptions}
          placeholder="Select Batch Main Location Type..."
          onChange={setMainLocationType}
          disabled={isEditDisabled}
        />
      </Col>
      <Col span={4}>
        <Select
          showSearch
          allowClear
          value={additionalLocationTypes}
          options={additionalLocationTypeOptions}
          placeholder="Select Batch Additional Location Types..."
          onChange={setAdditionalLocationTypes}
          disabled={isEditDisabled}
          dropdownStyle={{ maxHeight: 400, minWidth: 250 }}
          mode="multiple"
        />
      </Col>
      <Col span={4}>
        <TreeSelect
          multiple
          showSearch
          allowClear
          value={awards}
          treeData={awardOptions}
          placeholder="Select Batch Awards..."
          onChange={setAwards}
          disabled={isEditDisabled}
        />
      </Col>
      <Col span={4}>
        <TreeSelect
          showSearch
          allowClear
          value={cuisineType}
          treeData={cuisineTypeOptions}
          placeholder="Select Batch Cuisine Type..."
          onChange={setCuisineType}
          disabled={isEditDisabled}
        />
      </Col>
      <Col>
        {isSaveButtonVisible ? (
          <Button onClick={onSave} type="primary" disabled={isSaveDisabled}>
            Save
          </Button>
        ) : (
          <Popconfirm
            disabled={isSaveDisabled}
            onConfirm={onSave}
            title="This will clear out additional location types and awards, are you sure?"
          >
            <Button type="primary" disabled={isSaveDisabled}>
              Save
            </Button>
          </Popconfirm>
        )}
      </Col>
      <Col>
        <Button type="ghost" onClick={onCancel} disabled={isEditDisabled}>
          Cancel
        </Button>
      </Col>
      <Col>
        <Pagination {...paginationConfig} />
      </Col>
    </Row>
  )
}

LocationsTableFooter.propTypes = {
  isEditDisabled: PropTypes.bool,
  mainLocationTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  additionalLocationTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  cuisineTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  awardOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onBulkUpdateLocations: PropTypes.func.isRequired,
  paginationConfig: PropTypes.object,
}

LocationsTableFooter.defaultProps = {
  isEditDisabled: false,
  paginationConfig: {},
}

export default LocationsTableFooter
